import { logSwallowed } from '@/lib/errors';
import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Loader2, Settings, Volume2, VolumeX, X, UserPlus, Minimize2, Maximize2, Check } from 'lucide-react';
import { Room, RoomEvent, RemoteParticipant, RemoteAudioTrack, Track, Participant } from 'livekit-client';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { logEvent } from '@/lib/eventLog';
import { eventBusHub } from '@/lib/eventBusHub';
import { startRingback, stopRingtone } from '@/lib/ringtone';
import { settingsStore } from '@/lib/settingsStore';


export function RemoteVideo({ identity, speaking }: { identity: string; speaking?: boolean }) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const bgRef = useRef<HTMLVideoElement | null>(null);
  const [label, setLabel] = useState('');

  useEffect(() => {
    const room = (window as any).__hydraVideoRoom as Room | undefined;
    if (!room) return;
    let alive = true;
    const attach = () => {
      const p = room.remoteParticipants?.get(identity);
      if (!p) return;
      if (p.name) setLabel((prev) => (prev === p.name ? prev : String(p.name)));
      p.videoTrackPublications.forEach((pub) => {
        if (!pub.videoTrack) return;
        if (ref.current) {
          pub.videoTrack.attach(ref.current);
          if (alive) ref.current.play?.().catch((e) => { logSwallowed(e, 'components/chat/videoCallTiles.tsx:29'); return undefined; });
        }
        if (bgRef.current) pub.videoTrack.attach(bgRef.current);
      });
    };
    attach();
    const t1 = setTimeout(attach, 300);
    const t2 = setTimeout(attach, 1200);
    room.on(RoomEvent.TrackSubscribed, attach);
    room.on(RoomEvent.TrackUnmuted, attach);
    room.on(RoomEvent.ParticipantConnected, attach);
    return () => {
      alive = false;
      clearTimeout(t1);
      clearTimeout(t2);
      room.off(RoomEvent.TrackSubscribed, attach);
      room.off(RoomEvent.TrackUnmuted, attach);
      room.off(RoomEvent.ParticipantConnected, attach);
    };
  }, [identity]);

  return (
    <div className={`relative h-full w-full overflow-hidden bg-black ${speaking ? 'ring-2 ring-green-500 ring-inset' : ''}`}>
      {/* Размытая подложка (без обрезки лица) + основной кадр целиком (contain) */}
      <video ref={bgRef} autoPlay playsInline muted className="absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-2xl" />
      <video ref={ref} autoPlay playsInline className="relative h-full w-full object-contain" />
      <span className="absolute bottom-3 left-4 flex items-center gap-1.5 text-[12px] bg-black/50 rounded-full px-3 py-1">
        {label || 'Участник'}
        {speaking && <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />}
      </span>
    </div>
  );
}

// Звук удалённого участника: собственный <audio> (в core-SDK авто-плеера нет),
// привязка по событиям + volume из настроек; play() повторяем (политика автоплея).
export function RemoteAudio({ identity, volume }: { identity: string; volume: number }) {
  const ref = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const room = (window as any).__hydraVideoRoom as Room | undefined;
    if (!room) return;
    let alive = true;
    const attach = () => {
      const p = room.remoteParticipants?.get(identity);
      if (!p || !ref.current) return;
      p.audioTrackPublications.forEach((pub) => {
        if (pub.audioTrack) {
          pub.audioTrack.attach(ref.current!);
          if (alive) void ref.current!.play().catch((e) => { logSwallowed(e, 'components/chat/videoCallTiles.tsx:78'); return undefined; });
        }
      });
    };
    attach();
    const t1 = setTimeout(attach, 300);
    const t2 = setTimeout(attach, 1500);
    room.on(RoomEvent.TrackSubscribed, attach);
    room.on(RoomEvent.TrackUnmuted, attach);
    room.on(RoomEvent.ParticipantConnected, attach);
    return () => {
      alive = false;
      clearTimeout(t1);
      clearTimeout(t2);
      room.off(RoomEvent.TrackSubscribed, attach);
      room.off(RoomEvent.TrackUnmuted, attach);
      room.off(RoomEvent.ParticipantConnected, attach);
    };
  }, [identity]);

  useEffect(() => {
    if (ref.current) ref.current.volume = volume;
  }, [volume]);

  return <audio ref={ref} data-call-audio autoPlay playsInline className="hidden" />;
}
